import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { utils } from "./utils";
import { statsService, ApiLevelResponse, ApiTopicStat } from "../services/stats.service"; // Import new types
import { SkillData, TopicTableData } from "../../views/home/types";
import { getUserProfile } from "../redux/userSlice";
import { fetchAllCategories } from "../redux/categorySlice";


export const useReportsLogic = (student_id?: string | number) => {
    const dispatch: AppDispatch = useDispatch();

    const { categories, loading: categoriesLoading } = useSelector(
        (state: RootState) => state.categories
    );

    const { levels } = useSelector((state: RootState) => state.levels);
    const { user } = useSelector((state: RootState) => state.user);

    const [chartData, setChartData] = useState<SkillData[]>([]);
    const [isChartLoading, setIsChartLoading] = useState(false);
    const [currentChartIndex, setCurrentChartIndex] = useState(0);

    useEffect(() => {
        if (!user) {
            dispatch(getUserProfile());
        }
    }, [user, dispatch]);

    useEffect(() => {
        if (categories.length === 0 && !categoriesLoading) {
            dispatch(fetchAllCategories());
        }
    }, [categories, categoriesLoading, dispatch]);

    const nextChart = useCallback(() => {
        setCurrentChartIndex((prevIndex) => (prevIndex + 1) % chartData.length);
    }, [chartData.length]);

    const prevChart = useCallback(() => {
        setCurrentChartIndex((prevIndex) => (prevIndex - 1 + chartData.length) % chartData.length);
    }, [chartData.length]);

    const fetchChartData = useCallback(async () => {
        if (!levels.length || !user) {
            return;
        }

        setIsChartLoading(true);

        try {
            const promises = levels.map(level => (student_id ? statsService.getStudentStats(level.value, student_id) : statsService.getTopicStats(level.value)));
            const responses = await Promise.all(promises);


            const transformedChartData: SkillData[] = [];

            responses.forEach((response: ApiLevelResponse) => {
                if (!response || !response.level) return;

                const levelName = response.level.name;
                const apiTopics = response.level.topics || {};

                const categoryAggregator: Record<string, {
                    totalContent: number;
                    totalSuccess: number;
                    topicDetails: TopicTableData[];
                    id: number;
                }> = {};

                Object.values(apiTopics).forEach((topic: ApiTopicStat) => {
                    const apiCategories = topic.categories || {};

                    Object.values(apiCategories).forEach((catStat) => {
                        const catName = catStat.name;


                        if (!categoryAggregator[catName]) {
                            categoryAggregator[catName] = {
                                totalContent: 0,
                                totalSuccess: 0,
                                topicDetails: [],
                                id: catStat.id
                            };
                        }

                        categoryAggregator[catName].totalContent += catStat.content_count;
                        categoryAggregator[catName].totalSuccess += catStat.successfull_count;


                        if (catStat.content_count > 0) {
                            const successScore = Math.round((catStat.successfull_count / catStat.content_count) * 100);

                            categoryAggregator[catName].topicDetails.push({
                                key: `${topic.id}_${response.level.id}_${catStat.id}`,
                                name: topic.name,
                                totalActivity: catStat.content_count,
                                totalSuccess: catStat.successfull_count,
                                successScore: successScore
                            });
                        }
                    });
                });


                const finalLabels: string[] = [];
                const finalData: number[] = []; // Success Rates
                const finalTotalActivities: number[] = [];
                const finalTotalSuccesses: number[] = [];
                const finalTopicDetails: { [key: string]: TopicTableData[] } = {};

                Object.entries(categoryAggregator).forEach(([catName, data]) => {
                    // @ts-ignore
                    const isExcluded = (data.id === 11 || data.id === 12) && (utils.isUserStudent(user) && (!user?.classrooms || user.classrooms.length === 0));
                    if (isExcluded) return;

                    const successRate = data.totalContent > 0
                        ? (data.totalSuccess / data.totalContent) * 100
                        : 0;

                    finalLabels.push(catName);
                    finalData.push(Math.round(successRate));
                    finalTotalActivities.push(data.totalContent);
                    finalTotalSuccesses.push(data.totalSuccess);
                    finalTopicDetails[catName] = data.topicDetails;
                });

                const totalSuccessPercentage = finalData.reduce((a, b) => a + b, 0);

                if (totalSuccessPercentage > 0 || finalLabels.length > 0) {
                    transformedChartData.push({
                        title: levelName,
                        level: levelName,
                        labels: finalLabels,
                        data: finalData,
                        totalActivities: finalTotalActivities,
                        totalSuccesses: finalTotalSuccesses,
                        topicDetails: finalTopicDetails
                    });
                }
            });

            const filteredChartData = transformedChartData.filter(level => {
                const totalSuccessPercentage = level.data.reduce((sum, rate) => sum + rate, 0);
                return totalSuccessPercentage > 0;
            });

            setChartData(filteredChartData);
            setCurrentChartIndex(0);

        } catch (error) {
            console.error("Error fetching chart data:", error);
            setChartData([]);
        } finally {
            setIsChartLoading(false);
        }
    }, [levels, user]);

    useEffect(() => {
        fetchChartData();
    }, [fetchChartData]);

    const isLoading = isChartLoading || categoriesLoading || !user;

    return {
        user,
        chartData,
        isLoading,
        currentChartIndex,
        nextChart,
        prevChart,
        setCurrentChartIndex
    };
};